import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LinkedTicket } from "../../types/entities";
import { ConfigResponse } from "../../types/selfService";

// "closed" = no ticket item in the feed; "drafting" = the create_ticket feed
// item is showing the form, so the follow-up input edits the draft instead of
// asking a new question; "created" = the item is showing the filed ticket.
export type TicketDraftStage = "closed" | "drafting" | "created";

// "closed" = no chat item in the feed; "live" = connecting, queued or talking
// to an agent, so the follow-up input goes to the agent instead of asking the
// assistant; "ended" = the item shows the finished transcript.
export type AgentChatStage = "closed" | "live" | "ended";

type SliceStateType = {
  cssConfig: ConfigResponse | null;
  isSearchInputFocused: boolean;
  user: {
    email: string;
    name: string;
    ticketFields?: Record<string, string>;
  };
  ticketDraft: TicketDraftStage;
  // Index of the answer the ticket item belongs under, so it stays in place
  // when the conversation continues below it.
  ticketDraftIndex: number | null;
  // Set by the follow-up input, consumed (and cleared) by the ticket form.
  ticketEditInstruction: string | null;
  // Cases filed from this conversation, keyed by the index of the answer each
  // was filed from - straight off the answer response, so a reload renders
  // every card back in its place. Empty when none were filed.
  linkedTickets: Record<number, LinkedTicket>;
  agentChat: AgentChatStage;
  // Index of the answer the chat item renders under, as with the ticket item.
  agentChatIndex: number | null;
  // Set by the follow-up input while a chat is live, consumed (and cleared)
  // by the chat item, which sends it to the agent.
  agentChatOutgoing: string | null;
};

const initialState: SliceStateType = {
  cssConfig: {} as ConfigResponse,
  user: {
    email: "",
    name: ""
  },
  isSearchInputFocused: false,
  ticketDraft: "closed",
  ticketDraftIndex: null,
  ticketEditInstruction: null,
  linkedTickets: {},
  agentChat: "closed",
  agentChatIndex: null,
  agentChatOutgoing: null
};

const selfServiceSlice = createSlice({
  name: "selfService",
  initialState,
  reducers: {
    updateCssConfig: (state, action) => {
      state.cssConfig = action.payload;
    },
    setF1User: (state, action) => {
      // Merge, don't replace: getUser refreshes carry no ticketFields, and a
      // wholesale replace would wipe the host-page prefill stored below.
      //
      // Undefined keys have to be dropped, not just spread: the getUser
      // round-trip posts a user object with no ticketFields, so the payload
      // carries `ticketFields: undefined`, and spreading that overwrites the
      // stored prefill with undefined - the merge above looks like it guards
      // against this, and doesn't.
      const provided = Object.fromEntries(
        Object.entries(action.payload ?? {}).filter(
          ([, value]) => value !== undefined
        )
      );
      state.user = { ...state.user, ...provided };
    },
    // Parent-provided ticket prefill values (keyed by Salesforce Case field
    // API name). Kept separate from setF1User so it doesn't clobber the rest
    // of `user`.
    updateF1TicketFields: (state, action) => {
      state.user.ticketFields = action.payload;
    },
    setInputFocusChange: (state, action) => {
      state.isSearchInputFocused = action.payload;
    },
    // `payload` is the index of the answer the ticket is being filed from; the
    // item renders under that answer rather than after whatever is last.
    openTicketDraft: (state, action: PayloadAction<number>) => {
      state.ticketDraft = "drafting";
      state.ticketDraftIndex = action.payload;
      state.ticketEditInstruction = null;
    },
    setTicketDraftStage: (state, action: PayloadAction<TicketDraftStage>) => {
      state.ticketDraft = action.payload;
      if (action.payload === "closed") {
        state.ticketDraftIndex = null;
        state.ticketEditInstruction = null;
        // "closed" is dispatched when a new conversation starts, and a new
        // conversation has no tickets - keeping the old ones would show their
        // cards until the first response lands.
        state.linkedTickets = {};
        // Same reason: a chat belongs to the conversation it started in.
        state.agentChat = "closed";
        state.agentChatIndex = null;
        state.agentChatOutgoing = null;
      }
    },
    requestTicketEdit: (state, action: PayloadAction<string>) => {
      state.ticketEditInstruction = action.payload;
    },
    // User dismissed the draft form (the feed item's close/cancel button).
    // Unlike setTicketDraftStage("closed"), this leaves linkedTickets alone -
    // cancelling a new draft must not hide tickets already filed earlier in
    // this conversation.
    cancelTicketDraft: (state) => {
      state.ticketDraft = "closed";
      state.ticketDraftIndex = null;
      state.ticketEditInstruction = null;
    },
    clearTicketEdit: (state) => {
      state.ticketEditInstruction = null;
    },
    // `payload` is the index of the answer the chat is started from. Also
    // used to put a still-open chat back under its answer after a reload.
    openAgentChat: (state, action: PayloadAction<number>) => {
      state.agentChat = "live";
      state.agentChatIndex = action.payload;
      state.agentChatOutgoing = null;
    },
    endAgentChat: (state) => {
      state.agentChat = "ended";
      state.agentChatOutgoing = null;
    },
    // The customer closed the item before an agent ever answered, or the chat
    // could not start: nothing is left worth showing.
    closeAgentChat: (state) => {
      state.agentChat = "closed";
      state.agentChatIndex = null;
      state.agentChatOutgoing = null;
    },
    queueAgentChatMessage: (state, action: PayloadAction<string>) => {
      state.agentChatOutgoing = action.payload;
    },
    clearAgentChatMessage: (state) => {
      state.agentChatOutgoing = null;
    },
    // Replaces the whole map: it comes off the answer response, which is the
    // authority on what this conversation has filed.
    setLinkedTickets: (
      state,
      action: PayloadAction<Record<number, LinkedTicket>>
    ) => {
      state.linkedTickets = action.payload;
    },
    // One card, filed right now. The answer response only catches up on the
    // next refetch, and until it does the offer at that answer has to be gone
    // or a second ticket gets filed on top of the first.
    setLinkedTicket: (
      state,
      action: PayloadAction<{ index: number; ticket: LinkedTicket }>
    ) => {
      state.linkedTickets[action.payload.index] = action.payload.ticket;
    }
  }
});

export const {
  updateCssConfig,
  setF1User,
  updateF1TicketFields,
  setInputFocusChange,
  openTicketDraft,
  setTicketDraftStage,
  requestTicketEdit,
  clearTicketEdit,
  cancelTicketDraft,
  setLinkedTickets,
  setLinkedTicket,
  openAgentChat,
  endAgentChat,
  closeAgentChat,
  queueAgentChatMessage,
  clearAgentChatMessage
} = selfServiceSlice.actions;

export default selfServiceSlice.reducer;
